<template>
  <the-dialog-box
    ref="dialogBox"
    :submit="submit"
    :submit-btn-text="'Submit'"
    :cancel-btn-text="'Close'"
    :is-submit-btn="false"
    :full-screen="true"
    :persistent="true"
  >
    <template slot="header">
      <span>Raw Material Receiving PO# {{ inventoryPoNumber }}</span>
    </template>
    <template slot="body">
      <div
        v-if="inventoryPo"
        class="d-flex flex-column ga-5"
      >
        <div
          style="border: 1px solid #E4E4E7; border-radius: 10px;"
          class="d-flex flex-column ga-3 pa-4"
        >
          <h2 style="color: #000;">
            Order Details
          </h2>
          <div class="d-flex flex-wrap ga-5">
            <div style="min-width: 300px;">
              <span style="color: #000;">Class</span>
              <div
                style="border: 1px solid #E4E4E7; border-radius: 6px;"
                class="pa-3"
              >
                <v-chip
                  class="mt-1 mb-1"
                  small
                  text-color="white"
                  style="cursor: pointer; text-transform: uppercase"
                  :color="inventoryPo.class ? inventoryPo.class.color : 'white'"
                >
                  {{ inventoryPo.class ? inventoryPo.class.name : '' }}
                </v-chip>
              </div>
            </div>
            <div style="min-width: 300px;">
              <span style="color: #000;">Supplier</span>
              <div
                style="border: 1px solid #E4E4E7; border-radius: 6px;"
                class="pa-4"
              >
                <span
                  class="fw500"
                  style="color: #000;"
                >{{ inventoryPo.supplier_code ? inventoryPo.supplier_code : '' }} </span>
                <span
                  style="color: #000;"
                >{{ inventoryPo.supplier_name ? inventoryPo.supplier_name : '' }} </span>
              </div>
            </div>
            <div style="min-width: 300px;">
              <span v-if="businessRulesGetBoolean('Jobs Rename to Release')" style="color: #000;">Release</span>
              <span v-else style="color: #000;">Job</span>
              <div
                style="border: 1px solid #E4E4E7; border-radius: 6px;"
                class="pa-4"
              >
                <span
                  class="fw500"
                  style="color: #000;"
                >{{ inventoryPo.job_code ? inventoryPo.job_code : '' }} </span>
                <span
                  style="color: #000;"
                >{{ inventoryPo.job_name ? inventoryPo.job_name : '' }}</span>
                <span
                  style="color: #1976d2;"
                >; {{ inventoryPo.job_reference ? inventoryPo.job_reference : '' }} </span>
              </div>
            </div>
            <v-form ref="refForm" class="d-flex align-center ga-5" style="flex: 1 1 600px; min-width: 0;">
              <div style="width: 100%;">
                <span style="color: #000;">Packing Slip #<RedAsterisk /></span>
                <v-text-field
                  v-model="packageSlip"
                  outlined
                  compact
                  :rules="[(v) => !!v || 'Packing Slip # is required']"
                  placeholder="Enter Packing Slip #"
                />
              </div>
              <div style="width: 100%;">
                <span style="color: #000;">Packing Slip Image<RedAsterisk /></span>
                  <v-file-input
                    v-model="packageSlipImg"
                    accept="image/*"
                    capture="user"
                    outlined
                    compact
                    prepend-icon=""
                    placeholder="Enter Packing Slip Image"
                    :rules="[(v) => !!v || 'Packing Slip Image is required']"
                  >
                  </v-file-input>
              </div>
            </v-form>
          </div>
        </div>

        <div
          style="border: 1px solid #E4E4E7; border-radius: 10px;"
          class="d-flex flex-column ga-5 pa-4 w100"
        >
          <h2 style="color: #000;">
            Inventory Items
          </h2>
          <v-card
            elevation="0"
            outlined
          >
            <v-data-table
              :headers="headers"
              :items="inventoryPoItems"
              :items-per-page="-1"
              :loading="loading"
              hide-default-footer
              dense
            >
              <template #[`item.qr_code`]="{ item }">
                <span class="fw600">{{ getQrCode(item) }}</span>
              </template>
              <template #[`item.item`]="{ item }">
                {{ item.item_full_name ? item.item_full_name : '' }}
                <span v-if="item.inventory_supplier_item"> / {{ item.inventory_supplier_item.material_id }} - {{ item.inventory_supplier_item.name }}</span>
                <span v-if="isDesa() && item.inventory_item && item.inventory_item.inventory_tags.length > 0"> / {{ item.inventory_item.inventory_tags[item.inventory_item.inventory_tags.length - 1].inventory_tag }}</span>
              </template>
              <!-- eslint-disable-next-line vue/no-unused-vars -->
              <template #[`item.color`]="{ item }">
                {{ getColorCode(item) }}
              </template>
              <template #[`item.length`]="{ item }">
                {{ item.length ? item.length.name : '-' }}
              </template>
              <template #[`item.dimension_unit`]="{ item }">
                {{ item.dimension_unit ? item.dimension_unit.name : '-' }}
              </template>
              <template #[`item.uom`]="{ item }">
                {{ item.uom_name ? item.uom_name : '-' }}
              </template>
              <template v-if="isDesa()" #[`item.location`]="{ item }">
                <span v-if="item.inventory_item && item.inventory_item.inventory_tags && item.inventory_item.inventory_tags.length > 0">{{ item.inventory_item.inventory_tags[item.inventory_item.inventory_tags.length - 1].inventory_location.location.name }}</span>
              </template>
              <template v-if="isDesa()" #[`item.shelf`]="{ item }">
                <span v-if="item.inventory_item && item.inventory_item.inventory_tags && item.inventory_item.inventory_tags.length > 0">{{ item.inventory_item.inventory_tags[item.inventory_item.inventory_tags.length - 1].inventory_location.name }}</span>
              </template>
              <template #[`item.quantity`]="{ item }">
                {{ item.quantity ? item.quantity : '-' }}
              </template>
              <template #[`item.received_qty`]="{ item }">
                {{ getQtyReceived(item) }}
              </template>
              <template #[`item.difference_qty`]="{ item }">
                {{ getQtyDifference(item) }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  v-if="isNonQrCode(item)"
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  :disabled="!packageSlip"
                  @click="openEnterQuantity(item)"
                >
                  ENTER RECEIVED QUANTITY
                </v-btn>
                <!-- <v-btn
                  v-else-if="getQrCode(item)"
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  @click="openReceiving(item)"
                >
                  EDIT
                </v-btn> -->
                <v-btn
                  v-else
                  class="ml-1 mt-1 mb-1"
                  color="primary"
                  outlined
                  small
                  :disabled="!packageSlip || !packageSlipImg"
                  @click="openScanner(item)"
                >
                  SCAN QR
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </div>
      <v-dialog
        v-model="qrScannerDialog"
        fullscreen
      >
        <v-card class="d-flex fill-height flex-column">
          <v-card-text class="d-flex fill-height flex-column">
            <scanner-qr-form
              ref="scannerQrForm"
              :qr-type="'rm-receiving-qr'"
              @success="qrCodeSuccess"
            />
          </v-card-text>
          <v-card-actions>
            <div class="d-flex justify-end align-end w100">
              <v-btn
                @click="qrScannerDialog = false"
              >
                CLOSE
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="quantityDialog"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">Enter Received Quantity, {{ selectedItem && selectedItem.item_full_name ? selectedItem.item_full_name : '' }}</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="quantityForm">
              <v-text-field
                v-model="quantity"
                :rules="[(v) => !!v || 'Received Quantity is required']"
              >
                <template slot="label">
                  Received Quantity<RedAsterisk />
                </template>
              </v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <div class="d-flex ga-3 justify-end w100">
              <v-btn
                large
                @click="quantityDialog = false"
              >
                CLOSE
              </v-btn>
              <v-btn
                large
                color="primary"
                @click="submitQuantity"
              >
                SUBMIT
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </the-dialog-box>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ScannerQrForm from '@/components/elements/ScannerQrForm';
import { isDesa } from '../../../composables/desa';

export default {
  components: {
    ScannerQrForm,
  },
  props: {

  },
  data: () => ({
    inventoryPo: null,
    inventoryPoItems: [],
    inventoryPoNumber: null,
    selectedItem: null,
    quantity: null,
    // headers: [
    //   { text: isDesa() ? 'Desa Item/Supplier Item/QR Code' : 'Item', value: 'item' },
    //   { text: 'Color', value: 'color' },
    //   { text: 'Length', value: 'length' },
    //   { text: 'UOM', value: 'uom' },
    //   { text: 'Ordered Qty.', value: 'quantity' },
    //   { text: 'Received Qty.', value: 'received_qty' },
    //   { text: 'Difference Qty.', value: 'difference_qty' },
    //   { text: 'New QR Codes', value: 'qr_code' },
    //   { text: 'Actions', value: 'actions', align: 'end' },
    // ],
    loading: false,
    qrScannerDialog: false,
    quantityDialog: false,
    packageSlip: null,
    inventoryClassIdInst: 4,
    inventoryClassIdGlass: 5,
    inventoryClassIdPanels: 8,
    inventoryClassIdParts: 11,
    packageSlipImg: null,
    supplierItemsMap: {},
  }),
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),

    headers() {
      const headers = [
        {
          text: isDesa() ? 'Desa Item/Supplier Item/QR Code' : 'Item',
          value: 'item',
        },
        {
          text: 'Color',
          value: 'color',
        },
        {
          text: 'Length',
          value: 'length',
        },
        {
          text: 'UOM',
          value: 'uom',
        },
        {
          text: 'Ordered Qty.',
          value: 'quantity',
        },
        {
          text: 'Received Qty.',
          value: 'received_qty',
        },
        {
          text: 'Difference Qty.',
          value: 'difference_qty',
        },
        {
          text: 'New QR Codes',
          value: 'qr_code',
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'end',
        },
      ];

      let insertIndex = 3;

      if (
        this.inventoryPo &&
        this.inventoryPo.inventory_class_id === this.inventoryClassIdPanels
      ) {
        headers.splice(insertIndex, 0, {
          text: 'Dimension Unit',
          value: 'dimension_unit',
        });

        insertIndex++;
      }

      if (isDesa()) {
        headers.splice(
          insertIndex,
          0,
          {
            text: 'Location',
            value: 'location',
          },
          {
            text: 'Shelf',
            value: 'shelf',
          },
        );
      }

      return headers;
    },
  },
  watch: {

  },
  created() {
    this.item = { ...this.itemDto };
    // const inventoryClasses = this.$store.state.invClasses;
    // if (isDesa()) {
    //   this.headers.splice(3, 0, { text: 'Location', value: 'location' }, { text: 'Shelf', value: 'shelf' });
    // }
  },
  methods: {
    isDesa() {
      return isDesa();
    },  
    async get(inventoryPoNumber) {
      if (!inventoryPoNumber) return;
      const submitModel = {
        filters: {
          po_number: inventoryPoNumber,
        },
      };
      const res = await this.$api.inventoryPoItem.read(submitModel);
      if (!res || res.length < 1) return;
      this.inventoryPoItems = res;
      console.log(this.inventoryPoItems)
    },

    async getInventoryPo(inventoryPoNumber) {
      if (!inventoryPoNumber) return;
      const submitModel = {
        filters: {
          po_number: inventoryPoNumber,
        },
      };
      const res = await this.$api.inventoryPo.read(submitModel);
      if (!res || !res.data || res.data.length < 1) {
        this.$refs.dialogBox.close();
        this.$bus.$emit('showError', 'Error no inventory PO found');
        return;
      }
      const firstIndex = 0;
      const inventoryClasses = this.$store.state.invClasses;
      if (inventoryClasses && inventoryClasses.length > 0) {
        const checkclass = res.data[firstIndex].class = inventoryClasses.find((c) => c.id === res.data[firstIndex].inventory_class_id);
        if (!checkclass) {
          this.$refs.dialogBox.close();
          this.$bus.$emit('showError', 'Error you don\'t have access to this inventory class');
          return;
        }
      }

      this.inventoryPo = res.data[firstIndex];
    },

    close() {
      this.$refs.dialogBox.close();
    },

    async submit() {
      const valid = await this.$refs.refForm.validate();

      if (!valid) return false;

      const submitData = new FormData();

      // Qr Code is treated as inv tag for now
      if (this.inventoryPo) {
        submitData.append('inventory_po_id', this.inventoryPo.id);
      }

      if (this.packageSlip) {
        submitData.append('packaging_slip', this.packageSlip);
      }

      if (this.packageSlipImg) {
        submitData.append('packaging_slip_img', this.packageSlipImg);
      }

      const res = await this.$api.inventoryPoItem.store(submitData, 'receiving');

      if (!res) {
        this.$bus.$emit('showError', 'Error while receiving inventory items');
        return false;
      }
      this.packageSlip = null;
      this.$bus.$emit('showSuccess');
      this.$emit('success');
      return res;
    },

    async submitQuantity() {
      const valid = await this.$refs.quantityForm.validate();
      if (!valid) return;

      const submitData = {
        items: [{
          inventory_po_item_id: this.selectedItem.id,
          quantity: this.quantity,
        }],

      };

      const res = await this.$api.inventoryGoodEntrance.store(submitData);

      if (!res) {
        this.$bus.$emit('showError', 'Error while entering quantity');
        return;
      }
      this.quantityDialog = false;
      this.quantity = null;
      this.selectedItem = null;
      this.packageSlip = null;
      this.$bus.$emit('showSuccess');
      this.get(this.inventoryPoNumber);
    },

    open(inventoryPoNumber) {
      this.inventoryPoNumber = inventoryPoNumber;
      this.get(inventoryPoNumber);
      this.getInventoryPo(inventoryPoNumber);
      this.$refs.dialogBox.open();
      this.packageSlip = null;
    },

    openScanner(item) {
      this.qrScannerDialog = true;
      const inventoryClass = item.inventory_item ? item.inventory_item.inventory_class_id : item.inventory_po.inventory_class_id;
      let uom = item.uom_id;
      if (inventoryClass === this.inventoryClassIdGlass) {
        const uomPcsId = 1;
        uom = uomPcsId;
      }
      const data = {
        parameter_setting: item.inventory_item?.classes?.parameter,
        inventory_class_id: inventoryClass,
        inventory_item_id: item.inventory_item_id,
        glass_paint_code_id: item.glass_paint_code_id,
        uom_id: uom,
        length_id: item.length_id,
        dimension_unit_id: item.dimension_unit_id,
        color_id: this.inventoryPo.color_id ? this.inventoryPo.color_id : (item.repaint_to_color_id ? item.repaint_to_color_id : null),
        po_number: this.inventoryPoNumber,
        project_id: this.inventoryPo.job ? this.inventoryPo.job.project_id : null,
        inventory_po_item_id: item.id,
        location_id: this.inventoryPo.location_id,
        packaging_slip: this.packageSlip,
        packaging_slip_img: this.packageSlipImg ? this.packageSlipImg : null,
        inventory_po_id: this.inventoryPo.id,
      };
      this.$nextTick(async () => {
        this.$refs.scannerQrForm.setData(data);
      });
    },

    openEnterQuantity(item) {
      this.quantityDialog = true;
      this.selectedItem = item;
    },

    qrCodeSuccess() {
      this.qrScannerDialog = false;
      this.get(this.inventoryPoNumber);
    },

    getQrCode(item) {
      if (!item.good_entrances || item.good_entrances.length < 1) return '';
      // eslint-disable-next-line no-lone-blocks
      {
        // return inventory_tag from all entrances comma separated
        return [...new Set(item.good_entrances.map((e) => e.inventory_tag))].join(', ');
      }
    },

    getQtyReceived(item) {
      if (!item.good_entrances || item.good_entrances.length < 1) return 0;
      // eslint-disable-next-line no-lone-blocks
      {
        return item.good_entrances.reduce((acc, entrance) => acc + (entrance.quantity ? entrance.quantity : 0), 0);
      }
    },

    getQtyDifference(item) {
      const receivedQty = this.getQtyReceived(item);
      if (receivedQty === 0) return 0;
      return item.quantity - receivedQty;
    },

    isNonQrCode(item) {
      if (item.inventory_item && (item.inventory_item.inventory_class_id === this.inventoryClassIdInst || item.inventory_item.inventory_class_id === this.inventoryClassIdParts)
          && (item.inventory_item.material_id === 'EXTRA' || item.inventory_item.material_id === 'MISC')) {
        return true;
      }
      return false;
    },

    getColorCode(item) {
      if (this.businessRulesGetBoolean('Per Item Repaint Pricing') && item.repaint_to_color) {
        return item.repaint_to_color.code;
      }

      return this.inventoryPo && this.inventoryPo.color_code ? this.inventoryPo.color_code : '-';
    },
  },
};
</script>
